/**
 * derive.ts — the render rows of the task screen, from the story rows and the messages.
 *
 * The server mints `task_story` rows (the spine: a model-written sentence per step,
 * plus the phase rows); the message stream carries the blocks those sentences point at
 * (`blockRef` → `toolUseId`). This module joins the two and applies the reading rules
 * from the design spec (docs/superpowers/specs/2026-08-29-task-screen-retold-design.md
 * §2.1): the request opens, a plan item starting opens a chapter, every step is its
 * own row, prose and asks land where they happened.
 *
 * Pure and synchronous. It never composes a sentence — the sentence is the spine's.
 */
import type {
  ContentBlock,
  NoticeBlock,
  StoryEntry,
  ToolDisplay,
  ToolUseBlock,
} from "@bash/shared";
import { isTodoTool, terminalReasonFor } from "@bash/shared";
import type { TurnOutcome } from "@bash/shared";
import type { Message } from "@/types";
import type { PendingInteraction } from "@/stores/types";
import { isSwitchNet, isWaiting } from "@/lib/planTime";
import { parseSearchResult } from "@/components/blocks/tools/helpers";
import {
  ENGINE_INSTALLING_VERB,
  WORKSPACE_TROUBLE_SENTENCE,
  signInWaitVerb,
} from "@/lib/starting-copy";
import { planNameOf } from "@/lib/engine-plans";
import { isAgentMonthRefusal } from "@/lib/agent-month";

export type Family =
  | "term"
  | "files"
  | "read"
  | "search"
  | "web"
  | "browser"
  | "app"
  | "image"
  | "skill"
  | "team"
  | "mail"
  | "integration"
  | "memory";

const BY_NAME: Record<string, Family> = {
  Bash: "term",
  Monitor: "term",
  process: "term",
  Write: "files",
  Edit: "files",
  NotebookEdit: "files",
  Read: "read",
  Grep: "read",
  Glob: "read",
  WebSearch: "search",
  WebFetch: "web",
  announce_preview: "app",
  list_apps: "app",
  Skill: "skill",
  Agent: "team",
  Task: "team",
  mcp_invoke: "integration",
  mcp_search: "integration",
  remember: "memory",
};
const MAIL_APPS = new Set(["gmail", "outlook"]);

/** The three rooms of the user's brain, as the box mounts them. */
export type MemoryTier = "knowledge" | "notes" | "evidence";

/** A path inside the user's brain, wherever the box mounts it.
 *  `memory-of-a-user.md` is not one: the boundary is a whole path segment. The
 *  room DIRECTORY itself counts, since a Grep may name a bare room. */
const MEMORY_PATH_RE = /(^|\/)memory\/(knowledge|notes|evidence)(\/|$)/;

/**
 * The brain room a path sits in, or null. ONE judge, shared with the receipt
 * (`receipt.ts`): a step whose sentence says "from your brain" and a step whose
 * mark says `memory` must never disagree about what the brain is.
 */
export function memoryTierOf(
  path: string | null | undefined
): MemoryTier | null {
  if (!path) return null;
  return (MEMORY_PATH_RE.exec(path)?.[2] as MemoryTier | undefined) ?? null;
}

function underMemoryTree(toolInput?: Record<string, unknown> | null): boolean {
  if (!toolInput) return false;
  for (const key of ["file_path", "filePath", "path", "pattern"]) {
    const v = toolInput[key];
    if (typeof v === "string" && memoryTierOf(v)) return true;
  }
  return false;
}

/** Which mark a step wears. */
export function familyOf(
  toolName: string,
  display?: ToolDisplay | null,
  toolInput?: Record<string, unknown> | null
): Family {
  // P4 T2 — reading the brain is not reading the project. The user's own pages
  // arrive as plain files on the box, so only the PATH can tell the two apart.
  if (
    (toolName === "Read" || toolName === "Grep" || toolName === "Glob") &&
    underMemoryTree(toolInput)
  )
    return "memory";
  // The browser is decided by the TOOL, before any integration label: the runner's
  // tool-display stamps every MCP tool with its server slug ('browser-control')
  // as an integration, which used to file browser steps under the generic
  // four-squares glyph. Browsing wears Chrome (StoryIcons).
  if (
    toolName.startsWith("mcp__browser-control__") ||
    toolName.startsWith("computer_")
  )
    return "browser";
  if (display?.integration)
    return MAIL_APPS.has(display.integration) ? "mail" : "integration";
  if (
    /generate_(image|video|audio)|upscale|remove_background|higgsfield/i.test(
      toolName
    )
  )
    return "image";
  return (
    BY_NAME[toolName] ?? (toolName.startsWith("mcp__") ? "integration" : "term")
  );
}

/** The one line a family wears where no sentence of its own exists (section labels). */
export const FAMILY_LINE: Record<Family, string> = {
  term: "Running the setup",
  files: "Writing the page files",
  read: "Reading the project",
  search: "Searching the web",
  web: "Reading pages",
  browser: "Working in the browser",
  app: "Working with your apps",
  image: "Making the images",
  skill: "Using a skill",
  team: "Briefing the team",
  mail: "Working in your mail",
  integration: "Working in your apps",
  memory: "Saving to memory",
};

export type StepRow = {
  k: "step";
  entry: StoryEntry;
  family: Family;
  app: string | null;
  live: boolean;
  block: ToolUseBlock | null;
  /** The pages this step read. `title` and `excerpt` are what the search
   *  parser already knew about each one; the float's sources unfold titles its
   *  rows and quotes them, and the step's own chips read the domain alone. */
  sources: { domain: string; url: string; title: string; excerpt?: string }[];
  /** A detail the sentence does not carry (sender, subject, numbers). Reserved. */
  sub: string | null;
};
export type ChapterRow = {
  k: "chapter";
  text: string;
  done: boolean;
  rows: StoryRow[];
  steps: number;
  durationMs: number;
};
export type StoryRow =
  | { k: "you"; text: string; at: string; message: Message }
  | ChapterRow
  | StepRow
  | { k: "say"; text: string; messageId: string }
  | { k: "ask"; block: ContentBlock; messageId: string }
  | { k: "report"; message: Message }
  /** A budget trip (out of credits, a spent day, a task or turn limit): a card,
   *  never a silent drop (founder 09-02). */
  | { k: "budget"; triggeredBy: BudgetTrigger; text: string; messageId: string }
  /** One quiet line: a step the gate refused, or a plan heads-up (engines T0). */
  | { k: "notice"; block: NoticeBlock; messageId: string }
  | { k: "done"; kind: EndingKind; durationMs: number | null };

export type BudgetTrigger =
  "credits" | "task_limit" | "turn_ceiling" | "daily_ceiling" | "agent_month";

/** What tripped a max_budget block. No trigger reads as credits, as the error
 *  block renderer has always read it. Anything else is not a budget trip. */
function budgetTrigger(b: unknown): BudgetTrigger | null {
  const o = (b as { outcome?: { kind?: string; triggeredBy?: string } })
    .outcome;
  if (o?.kind !== "max_budget") return null;
  const t = o.triggeredBy;
  return t === "task_limit" ||
    t === "turn_ceiling" ||
    t === "daily_ceiling" ||
    t === "agent_month"
    ? t
    : "credits";
}

// ─── How a task ended ────────────────────────────────────────────────────────
// Three ways, and only one of them is a delivery. A stopped or failed task must
// never wear the delivery's words or its check (founder 07-21) — so the ending
// carries WHICH it was, and every surface reading it (the outcome, the desk)
// switches on that rather than on the mere existence of an ending.

// 'out_of_credits' (founder 09-03): the ledger ended the turn — nobody pressed
// Stop, nothing broke. It wears a stop's quiet ceremony and says the money ran
// out; it must never read "You stopped this task".
export type EndingKind = "done" | "stopped" | "out_of_credits" | "failed";
export interface Ending {
  kind: EndingKind;
  /** How long the FINAL turn took. Null when its start cannot be placed. */
  durationMs: number | null;
  /** When it ended (ms since epoch) — the terminal row's own time. Null when unparseable;
   *  absent on an ending built by hand (tests, fixtures). */
  at?: number | null;
  /** A failure's own words ("The selected model is unavailable") — the task
   *  row's error message or the runner's terminal error block. */
  text?: string | null;
  /** WHY a stop nobody pressed happened ("Budget limit reached") — the halt row's
   *  own line. Absent on the person's Stop, which reads as theirs. */
  title?: string;
}

/**
 * The failure the runner reported, read off the thread: an error block on the
 * NEWEST assistant message whose outcome the contract classifies as a genuine
 * failure. A budget trip is not one (it has its own card and its own ending);
 * a mid-turn error with no outcome is informational — the turn may have gone
 * on. An older failure a later turn recovered from is history. Its outcome
 * kind and the block's code ride along: the way forward the ending offers is
 * chosen from them, never from the words.
 */
export function terminalFailure(
  messages: Message[]
): { text: string; kind: TurnOutcome["kind"]; code?: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "assistant") continue;
    for (const b of m.content) {
      if (b.type !== "error") continue;
      const kind = (b as { outcome?: { kind?: TurnOutcome["kind"] } }).outcome
        ?.kind;
      if (!kind || kind === "max_budget") continue;
      const status = terminalReasonFor({
        kind,
        raw: { subtype: "", errors: [] },
      } as unknown as TurnOutcome).status;
      const code = (b as { code?: string }).code;
      if (status === "failed")
        return {
          text: (b as { error?: string }).error || "",
          kind,
          ...(code ? { code } : {}),
        };
    }
    return null;
  }
  return null;
}

const ENDING_OF: Partial<Record<NonNullable<StoryEntry["phase"]>, EndingKind>> =
  {
    done: "done",
    stopped: "stopped",
    out_of_credits: "out_of_credits",
    failed: "failed",
  };

/** Phase rows that close a turn — anything after one belongs to the NEXT turn.
 *  'waiting' counts: time parked on a HITL ask is the user's, not the turn's. */
const TURN_BOUNDARY_PHASES = new Set<StoryEntry["phase"]>([
  "done",
  "stopped",
  "out_of_credits",
  "failed",
  "waiting",
]);

/**
 * The terminal row's turn starts at the first row minted after the previous
 * boundary (each turn's dispatch mints a fresh 'working' phase row). Turn 1 has
 * no boundary and anchors on the task's birth — for a fresh task, queue + boot
 * time is part of what the user waited for. A later turn with no rows of its
 * own yields NaN (duration unknown) rather than lying with the task's age.
 */
function turnStartMs(
  rows: StoryEntry[],
  terminalIdx: number,
  taskCreatedAt: string | null
): number {
  let anchor: string | null = null;
  for (let i = terminalIdx - 1; i >= 0; i--) {
    const e = rows[i];
    if (e.kind === "phase" && e.phase && TURN_BOUNDARY_PHASES.has(e.phase)) {
      return anchor ? Date.parse(anchor) : NaN;
    }
    anchor = e.createdAt;
  }
  return taskCreatedAt ? Date.parse(taskCreatedAt) : NaN;
}

/**
 * How this task ended, or null if it has not.
 *
 * The NEWEST step-or-phase row decides — not the newest terminal one. A task
 * that finished, was asked again and then stopped ends STOPPED; one that
 * finished and is working again has not ended at all. Reading "is there a done
 * row anywhere" instead put a green delivery on both. (Pieces and plan rows are
 * skipped: they narrate, they never close a turn.)
 */
export function deriveEnding(
  entries: StoryEntry[],
  taskCreatedAt: string | null,
  /** The task's own word when the spine has no ending: a row that says error
   *  has failed (staging task aa525001, 09-04 — the error writer minted no
   *  'failed' row, and no ending + no steps read "done"). The spine wins
   *  whenever it has an ending; a spine 'failed' wears these words. */
  fallback?: {
    failed: boolean;
    text?: string | null;
    /** Engines T5: the task is paused on the person's own used-up plan (planPaused).
     *  Its last turn ended rate_limited, so the spine and the thread may both say
     *  failed; a pause is not a failure, so there is no ending yet. */
    paused?: boolean;
  }
): Ending | null {
  const ending = endingOf(entries, taskCreatedAt, fallback);
  return fallback?.paused && ending?.kind === "failed" ? null : ending;
}

/**
 * Engines T5 — is this task paused on the person's own used-up plan? A pending
 * plan_limit card (the head or queued behind another ask), or tasks.resume_at
 * still ahead (the person chose Wait, or the pre-flight is holding it for the
 * reset). Several accounts: a resume_at that IS the safety net a switch to the
 * next account armed (switchNetAt, the same instant) is never a pause.
 */
export function planPaused(
  task:
    | {
        pendingInteraction?: PendingInteraction | null;
        pendingQueue?: PendingInteraction[];
        resumeAt?: string | null;
        switchNetAt?: string | null;
      }
    | null
    | undefined,
  now: number = Date.now()
): boolean {
  if (!task) return false;
  // Only a resumeAt still ahead: a missed clear expires by itself.
  if (
    isWaiting(task.resumeAt, now) &&
    !isSwitchNet(task.resumeAt, task.switchNetAt)
  )
    return true;
  if (task.pendingInteraction?.type === "plan_limit") return true;
  return (task.pendingQueue ?? []).some((p) => p.type === "plan_limit");
}

function endingOf(
  entries: StoryEntry[],
  taskCreatedAt: string | null,
  fallback?: { failed: boolean; text?: string | null }
): Ending | null {
  const rows = [...entries].sort((a, b) => a.seq - b.seq);
  let terminalIdx = -1;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].kind === "step" || rows[i].kind === "phase") {
      terminalIdx = i;
      break;
    }
  }
  const failedByTask = (): Ending | null =>
    fallback?.failed
      ? {
          kind: "failed",
          durationMs: null,
          at: null,
          text: fallback.text ?? null,
        }
      : null;
  if (terminalIdx < 0) return failedByTask();

  let last = rows[terminalIdx];
  let kind =
    last.kind === "phase" && last.phase ? ENDING_OF[last.phase] : undefined;
  // A 'done' with no new work after another end IS that end, and the first one
  // is it. The same turn end wrote 'idle' twice (task 1a1bdd8e, 09-03 —
  // 'out_of_credits' then 'done' 107ms later), and a restart lost the server's
  // dedupe and closed an ended turn again 15 minutes later (task 4bda3e8b, 10-02
  // — the line read "16m 50s" for an 86 s turn). The server refuses both now;
  // older stories still carry them, and this reads them right. Only a 'done'
  // ever came twice: a halt after a finish keeps its halt.
  for (let i = terminalIdx - 1; i >= 0 && kind === "done"; i--) {
    const r = rows[i];
    if (r.kind === "step") break;
    if (r.kind !== "phase") continue;
    const prev = r.phase ? ENDING_OF[r.phase] : undefined;
    if (!prev) break;
    last = r;
    kind = prev;
    terminalIdx = i;
  }
  if (!kind) return failedByTask();

  // The final turn's own span — never the whole task's. A task that ran 20s,
  // sat idle for an hour and then ran 8s took 8s, not an hour.
  const started = turnStartMs(rows, terminalIdx, taskCreatedAt);
  const ended = Date.parse(last.createdAt);
  const durationMs =
    Number.isFinite(started) && Number.isFinite(ended) && ended > started
      ? ended - started
      : null;
  return {
    kind,
    durationMs,
    at: Number.isFinite(ended) ? ended : null,
    ...(kind === "failed" && fallback?.text ? { text: fallback.text } : {}),
    ...(kind === "stopped" && last.line ? { title: last.line } : {}),
  };
}

/** A plan item starting. Internal: it becomes a chapter and never leaves this module. */
type MilestoneRow = { k: "milestone"; text: string; done: boolean };
type Walked = StoryRow | MilestoneRow;
/** A row that happened between tool blocks; `ordinal` is the last block before it. */
type Beat = { ordinal: number; row: Walked };

const ms = (a?: string, b?: string) =>
  a && b ? Math.max(0, new Date(b).getTime() - new Date(a).getTime()) : 0;
const iso = (t: Date | string | null | undefined): string => {
  if (!t) return "";
  const d = t instanceof Date ? t : new Date(t);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

export function deriveRows(
  entries: StoryEntry[],
  messages: Message[],
  opts: {
    taskCreatedAt: string | null;
    ending: Ending | null;
  }
): StoryRow[] {
  // 1. One walk of the messages: index every tool block in order, and note where the
  //    prose, the asks, the reports and the plan items sit relative to those blocks.
  //    Todo blocks are the plan ledger, never a step — they mint milestones instead.
  const blocks = new Map<string, ToolUseBlock>();
  const ordinalOf = new Map<string, number>();
  const beats: Beat[] = [];
  let firstUser: Message | null = null;
  let cursor = -1;
  const seenNoticeRefs = new Set<string>();
  const subjects: string[] = [];
  // A plan item's id lives in TodoCreate's RESULT ({ taskId, subject }) and in
  // TodoUpdate's input (taskId). Pair by id; fall back to creation order only
  // for rows minted before results carried ids.
  const subjectById = new Map<string, string>();
  let started = 0;
  let pending: MilestoneRow | null = null;

  for (const m of messages) {
    if (m.role !== "assistant") {
      // The request is the message nothing precedes. A routine's run comes
      // first and is its own turn; a message sent after it is placed where it
      // happened — pinned above, it wore the run's steps as its own (founder,
      // 08-30: "the stream is continuing from previous rather than starting new").
      if (!firstUser && cursor === -1 && beats.length === 0) {
        firstUser = m;
        continue;
      }
      beats.push({ ordinal: cursor, row: { k: "report", message: m } });
      continue;
    }
    for (const b of m.content ?? []) {
      if (b.type === "tool_use") {
        if (isTodoTool(b.toolName)) {
          const input = (b.toolInput ?? {}) as Record<string, unknown>;
          const result = (
            b.result && typeof b.result === "object" ? b.result : {}
          ) as Record<string, unknown>;
          if (b.toolName.endsWith("Create")) {
            const subject = String(input.subject ?? input.content ?? "");
            subjects.push(subject);
            if (typeof result.taskId === "string" && subject)
              subjectById.set(result.taskId, subject);
          } else if (
            b.toolName.endsWith("Update") &&
            input.status === "in_progress"
          ) {
            // An update the ledger rejected ("unknown taskId") started nothing:
            // it must not consume a subject, or every chapter after it wears
            // the wrong name.
            if (typeof result.error === "string") continue;
            const id = typeof input.taskId === "string" ? input.taskId : null;
            const text =
              id && subjectById.has(id)
                ? subjectById.get(id)!
                : subjectById.size === 0
                  ? (subjects[started++] ?? "")
                  : "";
            if (text) {
              pending = { k: "milestone", text, done: false };
              beats.push({ ordinal: cursor, row: pending });
            }
          } else if (
            b.toolName.endsWith("Update") &&
            input.status === "completed" &&
            pending
          ) {
            pending.done = true;
          }
          continue;
        }
        ordinalOf.set(b.toolUseId, ++cursor);
        blocks.set(b.toolUseId, b);
      } else if (b.type === "text" && b.text.trim()) {
        beats.push({
          ordinal: cursor,
          row: { k: "say", text: b.text, messageId: m.id },
        });
      } else if (b.type === "question") {
        beats.push({
          ordinal: cursor,
          row: { k: "ask", block: b, messageId: m.id },
        });
      } else if (b.type === "error") {
        // A budget trip is a card, never a silent drop: an out-of-credits
        // refusal used to show "Bash is done" with nothing under it.
        const triggeredBy = budgetTrigger(b);
        // A spent month's sentence is the server's refusal's alone; an engine
        // trip's block is the web's synthesized label, so the card's own
        // generic line stands in for it.
        const text =
          triggeredBy === "agent_month" &&
          !isAgentMonthRefusal(b.code, b.outcome)
            ? ""
            : b.error;
        if (triggeredBy)
          beats.push({
            ordinal: cursor,
            row: { k: "budget", triggeredBy, text, messageId: m.id },
          });
      } else if (b.type === "notice") {
        // One line per ref per task: a stage-only denial repeats across turns (plan mode), and
        // the row key is the ref, so a second copy would also collide as a React key.
        if (!seenNoticeRefs.has(b.ref)) {
          seenNoticeRefs.add(b.ref);
          beats.push({
            ordinal: cursor,
            row: { k: "notice", block: b, messageId: m.id },
          });
        }
      }
    }
  }

  // 2. The steps, in spine order, each wearing what it touched. `seq` is the server's
  //    per-task monotonic truth; a realtime row appended out of order would otherwise
  //    misplace both the row and the live dot, so sort before reading anything.
  const allSteps = [...entries]
    .sort((a, b) => a.seq - b.seq)
    .filter((e) => e.kind === "step" && e.line);
  // Live means the newest step, still running. A row that never settled but
  // has newer steps after it (a question you already answered, a settle the
  // wire dropped) is history, not a pulse — the work moved on. So is one the
  // turn's ending came after: a stopped turn's ask is not "coming back"
  // (staging 10-02, task f517766b), and nothing runs past a turn's end.
  const newest = allSteps[allSteps.length - 1];
  const endedAfter =
    newest !== undefined &&
    entries.some(
      (e) =>
        e.kind === "phase" &&
        e.phase !== undefined &&
        ENDING_OF[e.phase] !== undefined &&
        e.seq > newest.seq
    );
  const lastRunning =
    newest && newest.status === "running" && !endedAfter ? newest : undefined;
  /** Slugs that name a surface the agent works THROUGH, never an app it works IN. */
  const NOT_AN_APP = new Set([
    "websearch",
    "webfetch",
    "browser-control",
    "browser",
  ]);
  // Every dispatch is its own step. Helpers sent out at once used to fold into
  // ONE row wearing the inline box that listed them; the box is gone (a helper
  // has its own screen now), and folding without it would leave every helper
  // but the first with no row at all.
  const stepRows: StepRow[] = allSteps.map((e) => {
    const block = (e.blockRef ? blocks.get(e.blockRef) : undefined) ?? null;
    const family = familyOf(
      block?.toolName ?? "",
      block?.display,
      (block?.toolInput ?? null) as Record<string, unknown> | null
    );
    const sources =
      family === "search" || family === "web"
        ? parseSearchResult(block?.result).sources.map((s) => ({
            domain: s.domain,
            url: s.url,
            title: s.title,
            excerpt: s.excerpt,
          }))
        : [];
    // A SURFACE is not an app. Rows minted before 08-30 carry 'websearch' as an
    // app, and the runner's tool-display labels every MCP tool with its server
    // slug ('browser-control') as an "integration" — no brand exists for either,
    // and the icon ladder's last rung painted a coloured letter (the green W, the
    // green B). The server refuses to mint them; this fallback must refuse them
    // too, so the family mark (magnifier, globe, Chrome) is their face.
    const raw = e.app ?? block?.display?.integration ?? null;
    const app = raw && NOT_AN_APP.has(raw.toLowerCase()) ? null : raw;
    const live = e === lastRunning;
    return {
      k: "step",
      entry: e,
      family,
      app,
      live,
      block,
      sources,
      sub: null,
    };
  });
  // Where each step sits among the blocks; a step whose block has not arrived keeps
  // the previous step's place rather than jumping the beats past it.
  const ordinals: number[] = [];
  let at = -1;
  for (const r of stepRows) {
    at = (r.entry.blockRef ? ordinalOf.get(r.entry.blockRef) : undefined) ?? at;
    ordinals.push(at);
  }

  // 3. Walk the steps, spending the beats that happened before each one. Beats are
  //    minted in message order, so their ordinals only ever rise — one cursor spends
  //    them, and nothing the agent said can be dropped.
  const rows: Walked[] = [];
  if (firstUser) {
    const text = (firstUser.content ?? [])
      .filter((b) => b.type === "text")
      .map((b) => b.text)
      .join("\n");
    rows.push({
      k: "you",
      text,
      at: iso(firstUser.timestamp) || iso(opts.taskCreatedAt),
      message: firstUser,
    });
  }
  let bi = 0;
  const beatSitsBefore = (ordinal: number) =>
    bi < beats.length && beats[bi].ordinal < ordinal;
  const spendBefore = (ordinal: number) => {
    while (beatSitsBefore(ordinal)) rows.push(beats[bi++].row);
  };

  let i = 0;
  // One flat flow (founder ruling, 08-30): every step is its own row. Nothing
  // folds into a '+N' — a reader following the work never has to open anything
  // to see what happened.
  while (i < stepRows.length) {
    spendBefore(ordinals[i]);
    rows.push(stepRows[i]);
    i++;
  }
  while (bi < beats.length) rows.push(beats[bi++].row);
  if (opts.ending)
    rows.push({
      k: "done",
      kind: opts.ending.kind,
      durationMs: opts.ending.durationMs,
    });

  return groupChapters(rows);
}

/** Each plan item owns the rows until the next one. The request and the outcome
 *  belong to no chapter; the renderer compacts a done chapter once the next one starts. */
function groupChapters(rows: Walked[]): StoryRow[] {
  const out: StoryRow[] = [];
  let cur: ChapterRow | null = null;
  for (const r of rows) {
    if (r.k === "milestone") {
      cur = {
        k: "chapter",
        text: r.text,
        done: r.done,
        rows: [],
        steps: 0,
        durationMs: 0,
      };
      out.push(cur);
    } else if (r.k === "you" || r.k === "report" || r.k === "done") {
      // Your words are never inside a chapter. A later message closes whatever
      // chapter was open — otherwise it lands inside the previous turn's last
      // chapter and folds away with it, and the reader watches their own
      // question vanish.
      cur = null;
      out.push(r);
    } else if (cur) {
      cur.rows.push(r);
      cur.steps += r.k === "step" ? 1 : 0;
      cur.durationMs +=
        r.k === "step" ? ms(r.entry.startedAt, r.entry.endedAt) : 0;
    } else {
      out.push(r);
    }
  }
  // An empty chapter is noise, not a rule: a re-plan mints fresh milestones with
  // the same names and the first turn's rules would stand above them with nothing
  // inside. Only the newest, still-open chapter may be empty — its work is landing.
  const lastChapter = [...out].reverse().find((r) => r.k === "chapter");
  return out.filter(
    (r) =>
      r.k !== "chapter" || r.rows.length > 0 || (!r.done && r === lastChapter)
  );
}

/** Every row in reading order, chapters opened. Consumers that look for the live step use this. */
export function flattenRows(rows: StoryRow[]): StoryRow[] {
  const out: StoryRow[] = [];
  for (const r of rows) {
    out.push(r);
    if (r.k === "chapter") out.push(...flattenRows(r.rows));
  }
  return out;
}

// 4. The desk: one mood and one verb for the whole task, read off the same rows
//    (design spec §2.3). Derived, never asked, from the live step's family, with
//    the phase (ask, team, done, a miss, asleep, typing) taking precedence.

export type Mood =
  | "content"
  | "working"
  | "thinking"
  | "reading"
  | "browsing"
  | "creating"
  | "waiting"
  | "happy"
  | "puzzled"
  | "listening"
  | "sleeping"
  | "waking";
/** `sentence`: a state that is a whole sentence of its own rather than "<subject> is
 *  <verb>" (an unreachable workspace); the line says it in place of the verb. */
export type DeskState = { mood: Mood; verb: string; sentence?: string };

/** The platform will not start the workspace; the server keeps trying (the presence
 *  repair `unreachable`). Not "reconnecting": nothing the person does helps, and the
 *  work continues by itself once the workspace answers. */
const WORKSPACE_TROUBLE_DESK: DeskState = {
  mood: "puzzled",
  verb: "having trouble starting",
  sentence: WORKSPACE_TROUBLE_SENTENCE,
};

/** The mood and verb a family wears while its step is the live one. */
/** What the desk says while a settled ask waits for its turn to reopen, by the
 *  ask's kind (TaskState.resumingFrom). The founder's own words for the connect. */
const RESUMING_DEFAULT: DeskState = {
  mood: "working",
  verb: "picking up where it left off",
};
const RESUMING_DESK: Record<string, DeskState> = {
  auth: { mood: "working", verb: "verifying the connection" },
  question: { mood: "working", verb: "picking up your answer" },
  permission: RESUMING_DEFAULT,
  approval: RESUMING_DEFAULT,
};

export const FAMILY_DESK: Record<Family, DeskState> = {
  term: { mood: "working", verb: "working" },
  files: { mood: "working", verb: "writing" },
  read: { mood: "reading", verb: "reading" },
  search: { mood: "reading", verb: "reading" },
  web: { mood: "reading", verb: "reading" },
  browser: { mood: "browsing", verb: "browsing" },
  app: { mood: "working", verb: "working" },
  image: { mood: "creating", verb: "creating" },
  skill: { mood: "working", verb: "working" },
  team: { mood: "working", verb: "briefing the team" },
  mail: { mood: "reading", verb: "working in your mail" },
  integration: { mood: "working", verb: "working in your apps" },
  memory: { mood: "thinking", verb: "remembering" },
};

/** The tools that pause the turn for the person; their step stays "live" until
 *  the answer resumes the turn. */
const ASK_TOOLS = new Set(["request_user_input", "AskUserQuestion"]);

/** The live step, wherever it sits. Chapters nest their rows (flatten first). */
function findLiveStep(flat: StoryRow[]): StepRow | null {
  return (
    flat.find((row): row is StepRow => row.k === "step" && row.live) ?? null
  );
}

/** Which step is live, by the spine's own id — the desk nods when this changes,
 *  because a new id IS a step landing. Null when nothing is running. */
export function liveStepId(rows: StoryRow[]): string | null {
  return findLiveStep(flattenRows(rows))?.entry.id ?? null;
}

export function deriveDesk(input: {
  rows: StoryRow[];
  isProcessing: boolean;
  pendingInteraction: PendingInteraction | null;
  lifecycleState: string | undefined;
  /**
   * §4.2 — "waiting on A, B and C": the SENTENCE for the lanes still out, or
   * "" when nobody is. Built by the caller (`waitingOnSentence` in
   * `lib/team-state.ts`, which reads FAMILY_DESK below): this file is the
   * leaf every state word grows from, and an import back would be a cycle.
   */
  teamWaitingOn: string;
  typing: boolean;
  lastErrorRecent: boolean;
  /** The link to the machine: 'retrying' while the socket is trying to come back, 'down' when it is not. */
  connection?: "ok" | "retrying" | "down";
  /** What the task is resuming from: its ask settled, the turn has not reopened (TaskState.resumingFrom). */
  resumingFrom?: string | null;
  /** The runner's AGENT_STATUS word (TaskState.sdkStatus): 'engine_connecting'
   *  while the box waits for the person to sign in to their plan (engines T2a),
   *  'engine_installing' while it installs the engine on its first use. */
  sdkStatus?: string | null;
  /** The task's engine (TaskState.runtime): the plan its sign-in names. */
  engine?: string | null;
  /** This task is waiting on a workspace the platform will not start (presence repair
   *  `unreachable`, and the task has work accepted or running). */
  workspaceTrouble?: boolean;
}): DeskState {
  const {
    rows,
    isProcessing,
    pendingInteraction,
    lifecycleState,
    teamWaitingOn,
    typing,
    lastErrorRecent,
    connection = "ok",
    resumingFrom = null,
    sdkStatus = null,
    engine = null,
    workspaceTrouble = false,
  } = input;
  // A dead link outranks everything: nothing typed is heard, nothing shown is
  // current, and a step still marked running may never settle.
  if (connection === "down") return { mood: "puzzled", verb: "offline" };
  // …except what the server has already said about the machine: a wake it could not
  // complete reads 'retrying' on the link, and here the reason is known.
  if (workspaceTrouble) return WORKSPACE_TROUBLE_DESK;
  if (connection === "retrying")
    return { mood: "puzzled", verb: "reconnecting" };
  // A machine still waking is a fact about the machine; it outranks what the
  // reader is doing at the keyboard. Nothing they type is heard until it is up.
  if (lifecycleState === "starting")
    return { mood: "waking", verb: "waking up" };
  if (lifecycleState === "updating")
    return { mood: "waking", verb: "updating" };
  if (typing) return { mood: "listening", verb: "listening" };
  if (pendingInteraction) return { mood: "waiting", verb: "waiting for you" };
  // The engine sign-in (engines T2a): before the turn opens the box has opened
  // the vendor's own sign-in and waits on the PERSON. There is no live step
  // yet, so without this the desk said "thinking" over a wait that was theirs.
  // Gated on a running turn: a word left on an idle task is not a wait.
  if (isProcessing && sdkStatus === "engine_connecting")
    return { mood: "waiting", verb: signInWaitVerb(planNameOf(engine)) };
  // The engine's first use on this computer: the box installs it before the
  // turn can start. Work on the machine, like an update, so the same mood.
  if (isProcessing && sdkStatus === "engine_installing")
    return { mood: "waking", verb: ENGINE_INSTALLING_VERB };
  // The ask settled and the turn has not reopened (TaskState.resumingFrom, set
  // on settle, cleared by AGENT_START): Bash is picking the work back up. It
  // outranks the step rules below on purpose — the paused tool's step is still
  // live (its result IS the resume), and answering flips the phase before the
  // turn opens — so without this the gap read "stuck", "coming back", or the
  // raw step name (founder 09-03, live on staging). Never "done".
  if (resumingFrom) return RESUMING_DESK[resumingFrom] ?? RESUMING_DEFAULT;
  const flat = flattenRows(rows);
  const live = findLiveStep(flat);
  // An ask Bash raised and you answered: the paused tool is still the live step
  // (its result is the resume itself), the row is resolved, and the resume is on
  // its way, often through a sleeping computer. That is neither stuck nor a step
  // in progress; it is Bash coming back (founder 09-02: a ticking "Enter your
  // password" step after Done invited three Stop clicks).
  if (live && ASK_TOOLS.has(live.block?.toolName ?? ""))
    return { mood: "thinking", verb: "coming back" };
  // A step still running while the task is idle is a settle the wire dropped
  // (the runner disconnected mid-step): the turn is stuck, not working.
  if (live && !isProcessing) return { mood: "puzzled", verb: "stuck" };
  if (live)
    return lastErrorRecent
      ? { mood: "puzzled", verb: "trying again" }
      : // D5 — the running verb wins while Bash's own turn runs (Bash IS
        // reading), with one exception: a delegate whose lane is already out
        // is not work, it is the wait itself, and "briefing the team" over a
        // helper that has been gone four minutes is the wrong word for it.
        live.family === "team" && teamWaitingOn
        ? { mood: "waiting", verb: teamWaitingOn }
        : FAMILY_DESK[live.family];
  // §4.2, D5 — the founder's rule (09-06): "show the exact state: Bash is
  // waiting on x, y, z, then x is done, waiting on y, z." ABOVE the ending on
  // purpose: a task with a lane still out is never done, never resting and
  // never stopped, whatever the story's last row says.
  if (teamWaitingOn) return { mood: "waiting", verb: teamWaitingOn };
  if (!isProcessing) {
    // The same law the outcome obeys: only a delivery is happy. A stopped or
    // failed task with a beaming face saying "done" is the green check by
    // another route.
    const ending = flat.find(
      (r): r is Extract<StoryRow, { k: "done" }> => r.k === "done"
    );
    if (ending?.kind === "done") return { mood: "happy", verb: "done" };
    if (ending?.kind === "stopped") return { mood: "content", verb: "stopped" };
    // The ledger's halt: calm face, honest words — never "stopped", which
    // reads as the user's own hand (founder 09-03).
    if (ending?.kind === "out_of_credits")
      return { mood: "content", verb: "out of credits" };
    if (ending?.kind === "failed") return { mood: "puzzled", verb: "stuck" };
    return { mood: "sleeping", verb: "resting" };
  }
  return { mood: "thinking", verb: "thinking" };
}

/** The desk is saying a work verb — a live step's family, or "thinking" — the
 *  only states the ladder (ladder.ts) may speak over. Every other state
 *  (waiting for you, waking up, updating, reconnecting, offline, stuck, trying
 *  again, listening, waiting on a lane, done) is a fact the ladder must not
 *  paint over. `deriveDesk` returns the family's own object, so identity is
 *  exact; "trying again" is a fresh literal and rightly fails it. */
export function deskIsWork(desk: DeskState): boolean {
  if (desk.mood === "thinking" && desk.verb === "thinking") return true;
  for (const d of Object.values(FAMILY_DESK)) if (d === desk) return true;
  return false;
}
